import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client';
import { companionsKeys } from './companions';
import { wardrobeKeys } from './wardrobe';

export type BagItemDto = {
  itemId: number;
  code: string;
  name: string;
  category: string;
  // Fix round 1 (review finding): `Item.subcategory`/`Item.description` are nullable columns
  // server-side — materials/cosmetics/dyes have no subcategory, and dyes have no description
  // (server/src/main/java/com/projectpets/items/Item.java) — the server sends a genuine JSON
  // `null`, not `""`. Widened from `string` to match, rather than pretending every item has one.
  subcategory: string | null;
  rarity: string;
  qty: number;
  stackable: boolean;
  tradeable: boolean;
  bound: boolean;
  description: string | null;
  usable: boolean;
  // Phase 4 Task 15 widening: BagController now also sends `slot` (Item.slot — already the same
  // column WardrobeController/EquipmentController read; null for categories that don't use it,
  // e.g. materials) and `levelReq` (equipment items only — parsed server-side from
  // `stats_json.levelReq`, the same field EquipmentService.equip() itself gates on; null for every
  // non-equipment category). Both optional (not just nullable) here, deliberately: every OTHER
  // BagItemDto fixture in this codebase (BagScreen/FeedSheet/HabitatScreen tests, gallery-entries)
  // predates this widening and would otherwise need updating just to keep compiling for two fields
  // nothing but the new EquipmentSheet picker reads — see that component's own header comment.
  slot?: string | null;
  levelReq?: number | null;
  // Phase 4 Task 16 widening (server: items/BagItemDto.java): category === 'consumable' only —
  // stats_json.type, this item's effect type ("heal"/"cleanse" for the two battle tonics, vs.
  // "care_restore"/"advance_stage" for every other consumable). The battle screen's Items drawer
  // filters the bag to category==='consumable' && (effectType==='heal'||effectType==='cleanse')
  // client-side using this field. Optional (not just nullable), same convention slot/levelReq
  // above already established — existing BagItemDto fixtures predate this widening.
  effectType?: string | null;
  // Item-detail-sheet widening (server: items/BagItemDto.java). `bonuses` is equipment-only —
  // `stats_json.bonuses`, the same object EffectiveStats sums — and `effectAmounts` is
  // consumable-only: that effect's own integers, keyed by whatever the effect shape names them
  // ({hunger: 45} for a care_restore, {hp: 80} for a heal). Both are `null`, never an empty map,
  // where the question doesn't apply or the effect genuinely has no numbers, so a falsy check is
  // enough to decide whether to render the block at all. Optional (not just nullable), the same
  // convention slot/levelReq/effectType above already established — every BagItemDto fixture in
  // this codebase predates this widening.
  bonuses?: Record<string, number> | null;
  effectAmounts?: Record<string, number> | null;
  // Item-detail-sheet widening (server: items/BagItemDto.java, Task 5 skill-craft UI polish):
  // where this item is made/gathered and what it feeds into, off the skill-craft content
  // (SkillCatalog) rather than any DB table. Mirrors server/.../items/CraftInfoDto.java field for
  // field. `@JsonInclude(NON_NULL)` on the server's `craft` component makes the key itself ABSENT
  // from the wire for any item entirely outside the skill-craft loop (dyes, cosmetics, tonics) —
  // never a fabricated empty object — hence optional here, same convention as every other widening
  // above. Within it, each sub-part is independently optional for the same reason: a raw material
  // may be made-not-gathered (`madeAt` only), gathered-not-made (`gatheredAt` only), both (a
  // material that is both smelted and mined, none exist in the shipped pack today but nothing
  // forbids it), or consumed by nothing further (`usedIn` genuinely absent, not `[]`).
  craft?: CraftInfoDto;
};

export type CraftIngredient = {
  itemCode: string;
  name: string;
  qty: number;
};

export type CraftMadeAt = {
  recipeCode: string;
  recipeName: string;
  benchCode: string;
  benchName: string;
  landCode: string;
  inputs: CraftIngredient[];
  minutes: number;
};

export type CraftGatheredAt = {
  nodeCode: string;
  activityCode: string;
  activityName: string;
  landCode: string;
  skillName: string;
  levelReq: number;
};

export type CraftUsedIn = {
  recipeCode: string;
  recipeName: string;
  outputItem: string;
  outputName: string;
  qty: number;
};

export type CraftInfoDto = {
  madeAt?: CraftMadeAt;
  gatheredAt?: CraftGatheredAt;
  usedIn?: CraftUsedIn[];
};

export type BagDto = {
  items: BagItemDto[];
};

export const bagKeys = {
  all: ['bag'] as const,
};

export const getBag = () => apiFetch<BagDto>('/bag');

export function useBag() {
  return useQuery({ queryKey: bagKeys.all, queryFn: getBag });
}

/** The codes of every non-stackable item the bag already holds — the only ownership a shop can
 *  refuse on (`already_owns_unique_item`; QA 2026-09-23, economy F4). Shared by the shop and
 *  stall screens, which read the same listing DTO. */
export function ownedUniqueCodes(items: { code: string; stackable: boolean }[] | undefined): Set<string> {
  return new Set((items ?? []).filter((i) => !i.stackable).map((i) => i.code));
}

// "Use an item" (POST /api/v1/items/use) lives in this module rather than a dedicated items.ts
// (not in the task-15 file list) since it's a bag-item action. The bare fetcher is named
// `postItemUse`, not `useItem...`, so it never reads as a hook to eslint-plugin-react-hooks (or
// a human skimming call sites) — `useUseItem` below is the actual hook.
export type UseItemInput = {
  itemCode: string;
  companionId: number;
};

export type UseItemResult = {
  stage: string;
};

export type UseItemResponse = {
  result: UseItemResult;
};

export const postItemUse = (input: UseItemInput) =>
  apiFetch<UseItemResponse>('/items/use', { method: 'POST', body: JSON.stringify(input) });

/** Not optimistic (the server decides whether the stage actually advances — item_not_usable/
 *  not_owned/wrong_stage are all real rejections a client can't predict). Invalidates the bag
 *  (the consumable is gone), the companions list (the companion's stage may have changed — this
 *  is what HabitatScreen's stage chip reads), AND (Task 17 fix) this specific companion's
 *  wardrobe query on success. That third one matters: `CompanionCard`'s live `Compositor` reads
 *  its `stage` prop from `useWardrobe(companion.id)`, not from the companions list — the compositor
 *  is Task 1/13's "one live paper-doll render" surface, keyed on the wardrobe API's own DTO. A
 *  stage-up tonic without this would leave the stage CHIP correct (companions invalidated) while
 *  the companion's ART silently kept rendering the old stage's base image until something else
 *  happened to refetch the wardrobe query (e.g. opening /wardrobe) — an easy-to-miss staleness
 *  bug since nothing looks visibly broken, just quietly wrong. */
export function useUseItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postItemUse,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: bagKeys.all });
      queryClient.invalidateQueries({ queryKey: companionsKeys.all });
      queryClient.invalidateQueries({ queryKey: wardrobeKeys.detail(variables.companionId) });
    },
  });
}
